import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApi, postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import {
  tenantHasUploadedDocuments,
  TENANT_DOCUMENTS_REQUIRED_MESSAGE,
} from '../../Utils/tenantDocuments';
import PropertyImageModal from '../../LandlordComponent/PropertiesComponent/PropertyDetail/PropertyImageModal';
import TenantDocumentsUploadModal from './TenantDocumentsUploadModal';
import LeaseSignCard from './LeaseSignCard';
import { LEASE_STATUS } from '../../Services/leaseService';
import { CHECKR_STATUS, getCheckrStatusLabel } from '../../Utils/checkrStatus';
import {
  BACKGROUND_CHECK_URL,
  BackgroundCheckPaymentForm,
  startBackgroundCheckPayment,
} from '../SettingsComponent/BackgroundCheckComponent/BackgroundCheckPaymentForm';
import {
  getCallStatusLabel,
} from '../ScreeningsComponent/screeningEnums';

/** Prefill the e-signature with the tenant's own name from the stored profile. */
const getSignerName = () => {
  try {
    const userDataString = localStorage.getItem('userData');
    if (!userDataString) return '';
    const userDetails = JSON.parse(userDataString)?.user_details || {};
    return [userDetails.first_name, userDetails.last_name].filter(Boolean).join(' ');
  } catch (error) {
    console.error('Error reading stored user data:', error);
    return '';
  }
};

/** The API sends flags as true, "1" or "true" depending on the field. */
const isTruthyFlag = (value) =>
  value === true || String(value) === '1' || String(value).toLowerCase() === 'true';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [tenantExtra, setTenantExtra] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [llcLogoError, setLlcLogoError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [propertyAmenityIds, setPropertyAmenityIds] = useState([]);
  const [propertyImages, setPropertyImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [hasTenantDocuments, setHasTenantDocuments] = useState(null);
  const [isCheckingDocuments, setIsCheckingDocuments] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [isPropertyAllocated, setIsPropertyAllocated] = useState(false);
  const [isSubscribingRent, setIsSubscribingRent] = useState(false);
  const [rentSubscription, setRentSubscription] = useState(null);
  const [hasActiveLease, setHasActiveLease] = useState(false);
  const [isLeaseSigned, setIsLeaseSigned] = useState(false);
  const [leaseId, setLeaseId] = useState('');
  const [isCancellingRent, setIsCancellingRent] = useState(false);
  const [isCancelRentModalOpen, setIsCancelRentModalOpen] = useState(false);
  const [backgroundCheckStatus, setBackgroundCheckStatus] = useState(null);
  const [backgroundCheckDescription, setBackgroundCheckDescription] = useState('');
  const [backgroundCheckApplicationUrl, setBackgroundCheckApplicationUrl] = useState('');
  const [isStartingBackgroundCheck, setIsStartingBackgroundCheck] = useState(false);
  const [backgroundCheckPayment, setBackgroundCheckPayment] = useState(null); // { clientSecret, amount }
  const carouselIntervalRef = useRef(null);

  // Property type mapping (same as landlord)
  const propertyTypeMap = {
    '1': 'Apartment',
    '2': 'House',
    '3': 'Townhouse',
    '4': 'Studio'
  };

  // Fetch property details then amenities metadata (same pattern as landlord detail)
  useEffect(() => {
    const transformProperty = (apiData) => {
      const images = apiData.property_images && apiData.property_images.length > 0
        ? apiData.property_images.map(img => img.image_url)
        : ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'];
      const firstImage = images[0];

      const address = apiData.address || '';
      const unitNumber = apiData.unit_number ? `, ${apiData.unit_number}` : '';
      const fullAddress = `${address}${unitNumber}`;

      const cityState = apiData.city && apiData.state
        ? `${apiData.city}, ${apiData.state}`
        : apiData.city || apiData.state || '';

      const price = apiData.monthly_rent
        ? `$${Number(apiData.monthly_rent).toLocaleString()}`
        : 'N/A';

      const sqft = apiData.square_feet
        ? `${Number(apiData.square_feet).toLocaleString()} sq.ft`
        : 'N/A';

      const type = propertyTypeMap[apiData.property_type] || 'Apartment';

      const deposit = apiData.security_deposit
        ? `$${Number(apiData.security_deposit).toLocaleString()}`
        : 'N/A';

      const code = apiData.property_code || (apiData.id ? String(apiData.id).substring(0, 6) : '');

      return {
        id: apiData.id,
        image: firstImage,
        address: fullAddress,
        code,
        city: cityState,
        zipCode: apiData.zip_code || '',
        price,
        monthlyRent: apiData.monthly_rent != null ? Number(apiData.monthly_rent) : null,
        sqft,
        type,
        deposit,
        securityDeposit: deposit,
        bedrooms: apiData.bedrooms || 0,
        bathrooms: apiData.bathrooms || 0,
        description: apiData.description || '',
        llcName: apiData.llc_name || '',
        llcLogo: apiData.llc_logo || ''
      };
    };

    const fetchProperty = async () => {
      if (!id) {
        setIsLoading(false);
        navigate('/tenant/properties');
        return;
      }

      setIsLoading(true);
      try {
        const response = await getApi(`/property/${id}`);

        if (response?.statusCode === 200 && response?.data) {
          let parsedAmenityIds = [];
          try {
            if (response.data.amenities) {
              const parsed = typeof response.data.amenities === 'string'
                ? JSON.parse(response.data.amenities)
                : response.data.amenities;
              if (Array.isArray(parsed)) {
                parsedAmenityIds = parsed.map(a =>
                  typeof a === 'object' && a?.id != null ? String(a.id) : String(a)
                );
              }
            }
          } catch (e) {
            console.error('Error parsing property amenities:', e);
          }

          setPropertyAmenityIds(parsedAmenityIds);
          setProperty(transformProperty(response.data));
          setTenantExtra(response.data.tenant_extra || null);
          setBackgroundCheckStatus(response.data.background_check_status ?? null);
          setBackgroundCheckDescription(response.data.background_check_description || '');
          // property_allocated comes back true only for the tenant this property was allotted to
          setIsPropertyAllocated(isTruthyFlag(response.data.property_allocated));
          // has_active_lease true = this tenant already subscribed to monthly rent
          setHasActiveLease(isTruthyFlag(response.data.has_active_lease));
          // lease_signed true = the lease agreement is signed, which is what unlocks
          // the rent subscription. The flag may also arrive on the nested lease row.
          setIsLeaseSigned(
            isTruthyFlag(response.data.lease_signed) ||
            isTruthyFlag(response.data.is_lease_signed) ||
            String(response.data.lease_status ?? '') === LEASE_STATUS.SIGNED ||
            String(response.data.lease?.status ?? '') === LEASE_STATUS.SIGNED
          );
          setLeaseId(
            response.data.lease_id ||
            response.data.active_lease?.id ||
            response.data.lease?.id ||
            ''
          );

          const images = response.data.property_images && response.data.property_images.length > 0
            ? response.data.property_images.map(img => img.image_url)
            : ['https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800'];
          setPropertyImages(images);
          setCurrentImageIndex(0);

          // Fetch amenities metadata by property_type (same as landlord) for Property Features
          const propertyType = response.data.property_type != null ? String(response.data.property_type) : null;
          if (propertyType) {
            try {
              const metaResponse = await getApi('/property/metadata', {
                params: { property_type: propertyType }
              });
              if (metaResponse?.statusCode === 200 && metaResponse?.data?.amenities) {
                const amenities = metaResponse.data.amenities
                  .filter(amenity => amenity.is_active === '1')
                  .map(amenity => ({ id: String(amenity.id), name: amenity.name }));
                setAmenitiesList(amenities);
              }
            } catch (metaError) {
              console.error('Error fetching amenities metadata:', metaError);
              setAmenitiesList([]);
            }
          }
        } else {
          toast.error(response?.message || 'Failed to fetch property details');
          navigate('/tenant/properties');
        }
      } catch (error) {
        console.error('Error fetching property:', error);
        toast.error('Failed to load property details. Please try again.');
        navigate('/tenant/properties');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProperty();
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const loadTenantDocumentStatus = async () => {
      try {
        const hasDocs = await tenantHasUploadedDocuments();
        if (!cancelled) setHasTenantDocuments(hasDocs);
      } catch (error) {
        console.error('Error checking tenant documents:', error);
        if (!cancelled) setHasTenantDocuments(null);
      }
    };

    loadTenantDocumentStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-rotate carousel (pause when modal is open)
  useEffect(() => {
    if (propertyImages.length > 1 && !isImageModalOpen) {
      carouselIntervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
      }, 5000);
    } else if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
    }
    return () => {
      if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    };
  }, [propertyImages.length, isImageModalOpen]);

  const goToNextImage = () => {
    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
    setTimeout(() => {
      if (propertyImages.length > 1 && !isImageModalOpen) {
        carouselIntervalRef.current = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
        }, 5000);
      }
    }, 10000);
  };

  const goToPrevImage = () => {
    if (carouselIntervalRef.current) clearInterval(carouselIntervalRef.current);
    setCurrentImageIndex((prev) => (prev - 1 + propertyImages.length) % propertyImages.length);
    setTimeout(() => {
      if (propertyImages.length > 1 && !isImageModalOpen) {
        carouselIntervalRef.current = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % propertyImages.length);
        }, 5000);
      }
    }, 10000);
  };

  const openImageModal = (index) => {
    setModalImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => setIsImageModalOpen(false);

  const goToNextModalImage = () => {
    setModalImageIndex((prev) => (prev + 1) % propertyImages.length);
  };

  const goToPrevModalImage = () => {
    setModalImageIndex((prev) => (prev - 1 + propertyImages.length) % propertyImages.length);
  };

  const proceedToScreening = () => {
    if (isPaymentDone) {
      navigate(`/tenant/properties/${id}/screening/complete-payment`, {
        state: {
          paymentAlreadyDone: true,
          propertyAddress: property.address,
          propertyCode: property.code || '',
        },
      });
      return;
    }
    navigate(`/tenant/properties/${id}/screening`, {
      state: {
        propertyAddress: property.address,
        propertyCode: property.code || '',
      },
    });
  };

  const handleStartScreening = async () => {
    setIsCheckingDocuments(true);
    try {
      const hasDocs = await tenantHasUploadedDocuments();
      setHasTenantDocuments(hasDocs);

      if (!hasDocs) {
        toast.error(TENANT_DOCUMENTS_REQUIRED_MESSAGE);
        setIsDocumentsModalOpen(true);
        return;
      }

      proceedToScreening();
    } catch (error) {
      console.error('Error verifying tenant documents:', error);
      toast.error('Unable to verify your documents. Please try again.');
    } finally {
      setIsCheckingDocuments(false);
    }
  };

  // The allotted tenant can start paying rent monthly from here.
  // POST /rent/subscribe -> { lease_id, subscription_id, rent_amount, client_secret, requires_payment }
  // A requires_payment response still needs the card confirmation step before rent is actually collected.
  const handleSubscribeMonthlyRent = async () => {
    if (isSubscribingRent) return;
    // Rent can only start once the lease agreement is signed.
    if (!isLeaseSigned) {
      toast.info('Please sign the lease agreement before subscribing to monthly rent.');
      return;
    }

    setIsSubscribingRent(true);
    try {
      const response = await postApi('/rent/subscribe', { property_id: id });

      if (response?.statusCode === 200) {
        const data = response?.data || {};
        const clientSecret = data.client_secret || data.clientSecret || '';
        const requiresPayment = data.requires_payment === true || String(data.requires_payment) === '1';

        setLeaseId(data.lease_id || '');
        setRentSubscription({
          leaseId: data.lease_id || '',
          subscriptionId: data.subscription_id || '',
          rentAmount: data.rent_amount != null ? data.rent_amount : null,
          clientSecret,
          requiresPayment,
        });

        // Card still has to be confirmed — same payment step the screening flow uses.
        if (clientSecret) {
          navigate(`/tenant/properties/${id}/rent/complete-payment`, {
            state: {
              clientSecret,
              rentAmount: data.rent_amount != null ? data.rent_amount : property?.monthlyRent,
              propertyAddress: property?.address,
              propertyCode: property?.code || '',
              leaseId: data.lease_id || '',
              subscriptionId: data.subscription_id || '',
            },
          });
          return;
        }

        if (requiresPayment) {
          toast.error('Payment could not be started for this subscription. Please try again.');
          return;
        }

        setHasActiveLease(true);
        toast.success(response?.message || 'Rent subscription created');
      } else {
        toast.error(response?.message || 'Failed to subscribe to monthly rent');
      }
    } catch (error) {
      console.error('Error subscribing to monthly rent:', error);
      // 400 = missing property / no rent set / landlord not connected / already subscribed
      toast.error(
        error?.response?.data?.message || 'Failed to subscribe to monthly rent. Please try again.'
      );
    } finally {
      setIsSubscribingRent(false);
    }
  };

  // /rent/cancel needs the lease id. The subscribe response carries it; on a reload
  // the property only reports has_active_lease, so fall back to the tenant's lease list.
  const resolveLeaseId = async () => {
    const known = rentSubscription?.leaseId || leaseId;
    if (known) return known;

    try {
      const response = await getApi('/rent/my-leases');
      const payload = response?.data;
      const leases = Array.isArray(payload)
        ? payload
        : payload?.leases || payload?.rows || payload?.data || [];

      const match = leases.find(
        (lease) => String(lease?.property_id || lease?.property?.id || '') === String(id)
      );
      const found = match?.lease_id || match?.id || '';

      if (found) setLeaseId(found);
      return found;
    } catch (error) {
      console.error('Error loading rent leases:', error);
      return '';
    }
  };

  // Background check for this property: status + Checkr form link (application_url while waiting_for_applicant)
  const fetchBackgroundCheck = async () => {
    try {
      const res = await getApi(BACKGROUND_CHECK_URL, { params: { property_id: id } });
      const data = res?.data || {};
      setBackgroundCheckStatus(data.status ?? null);
      setBackgroundCheckApplicationUrl(data.application_url || '');
    } catch (error) {
      console.error('Error fetching background check:', error);
    }
  };

  useEffect(() => {
    if (id && backgroundCheckStatus === CHECKR_STATUS.WAITING_FOR_APPLICANT && !backgroundCheckApplicationUrl) {
      fetchBackgroundCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, backgroundCheckStatus]);

  const handleStartBackgroundCheck = async () => {
    setIsStartingBackgroundCheck(true);
    try {
      const result = await startBackgroundCheckPayment(id);
      if (result.alreadyPaid) {
        toast.success(result.message || 'Your background check is already paid');
        fetchBackgroundCheck();
        return;
      }
      setBackgroundCheckPayment({ clientSecret: result.clientSecret, amount: result.amount });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Unable to start background check');
    } finally {
      setIsStartingBackgroundCheck(false);
    }
  };

  const handleBackgroundCheckPaymentSuccess = () => {
    setBackgroundCheckPayment(null);
    fetchBackgroundCheck();
  };

  const openCancelRentModal = () => setIsCancelRentModalOpen(true);
  const closeCancelRentModal = () => setIsCancelRentModalOpen(false);

  const confirmCancelRentSubscription = async () => {
    setIsCancelRentModalOpen(false);
    if (isCancellingRent) return;

    setIsCancellingRent(true);
    try {
      const lease = await resolveLeaseId();
      if (!lease) {
        toast.error('Lease not found for this property');
        return;
      }

      const response = await postApi('/rent/cancel', { lease_id: lease });

      if (response?.statusCode === 200) {
        setHasActiveLease(false);
        setRentSubscription(null);
        setLeaseId('');
        toast.success(response?.message || 'Rent subscription cancelled');
      } else {
        toast.error(response?.message || 'Failed to cancel rent subscription');
      }
    } catch (error) {
      console.error('Error cancelling rent subscription:', error);
      // 404 = lease not found
      toast.error(
        error?.response?.data?.message || 'Failed to cancel rent subscription. Please try again.'
      );
    } finally {
      setIsCancellingRent(false);
    }
  };

  // is_payment 0 = payment pending → screening info → payment form
  // is_payment 1 = payment done → skip directly to Begin Application Screening
  const isPaymentDone =
    tenantExtra && String(tenantExtra.is_payment) === '1';
  // Property already allotted to someone (final_screening_status 1) – no screening available
  const propertyAllotted =
    tenantExtra && String(tenantExtra.final_screening_status) === '1';
  // ...and property_allocated true means it was allotted to the logged-in tenant
  const allottedToMe = propertyAllotted && isPropertyAllocated;
  // Voice: 0:pending, 1:calling, 2:completed, 3:failed
  // document_status: 0:pending, 1:completed
  // final_screening_status: 0:pending, 1:completed
  const screeningDone = tenantExtra && String(tenantExtra.screening_call_status) === '2';
  const documentStatusLabel = tenantExtra
    ? (String(tenantExtra.document_status) === '1' ? 'Completed' : String(tenantExtra.document_status) === '2' ? 'Rejected' : 'Pending')
    : 'Pending';
  const finalDecisionLabel = tenantExtra
    ? (String(tenantExtra.final_screening_status) === '1' ? 'Completed' : String(tenantExtra.final_screening_status) === '2' ? 'Rejected' : 'Pending')
    : 'Pending';

  const statusSteps = [
    { name: 'Screening', status: getCallStatusLabel(tenantExtra?.screening_call_status) },
    { name: 'Documents for verification', status: documentStatusLabel },
    { name: 'Final Decision', status: finalDecisionLabel },
    { name: 'Background Check', status: getCheckrStatusLabel(backgroundCheckStatus), checkrStatus: backgroundCheckStatus ?? 'none' },
  ];
  const backgroundCheckLink = (tenantExtra && (tenantExtra.background_check_link || tenantExtra.background_check_url)) || '';

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'verified' || s === 'done') return '#155724';
    if (s === 'rejected' || s === 'failed') return '#721c24';
    if (s === 'calling') return '#004085';
    return '#856404'; // Pending
  };
  const getStatusBgColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'verified' || s === 'done') return '#d4edda';
    if (s === 'rejected' || s === 'failed') return '#f8d7da';
    if (s === 'calling') return '#cce5ff';
    return 'rgba(191, 140, 67, 0.2)'; // Pending
  };

  const handleCopyBackgroundLink = () => {
    const link = backgroundCheckLink;
    if (!link) return;
    if (navigator?.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(link);
      toast.success('Link copied');
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = link;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      toast.success('Link copied');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleOpenBackgroundLink = () => {
    const link = backgroundCheckLink;
    if (!link) return;
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  if (isLoading || !property) {
    return (
      <div className="property-detail-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div>Loading property details...</div>
      </div>
    );
  }

  return (
    <div className="property-detail-page">
      <div className="property-detail-container">
        <div className="property-detail-left">
          <div className="property-detail-header">
            <div className="property-detail-address-section">
              <h1 className="property-detail-address">{property.address}</h1>
              <div className="property-detail-code-location">
                <span className="property-detail-location">{property.city} {property.zipCode}</span>
              </div>
              {(property.llcLogo || property.llcName) && (
                <div className="property-detail-llc">
                  {property.llcLogo && !llcLogoError ? (
                    <img
                      src={property.llcLogo}
                      alt={property.llcName ? `${property.llcName} logo` : 'Business logo'}
                      className="property-detail-llc-logo"
                      onError={() => setLlcLogoError(true)}
                    />
                  ) : (
                    <span className="property-detail-llc-fallback" aria-hidden="true">
                      {(property.llcName || '?').trim().charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="property-detail-llc-text">
                    <span className="property-detail-llc-label">Managed by</span>
                    <span className="property-detail-llc-name">{property.llcName || 'Business/LLC'}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="property-detail-image-container">
            {propertyImages.length > 0 ? (
              <div className="property-detail-carousel" style={{ position: 'relative', width: '100%' }}>
                <div className="property-detail-carousel-image-container" style={{ position: 'relative', width: '100%', height: '400px' }}>
                  <img
                    src={propertyImages[currentImageIndex]}
                    alt={`${property.address} - Image ${currentImageIndex + 1}`}
                    className="property-detail-image"
                    onClick={() => openImageModal(currentImageIndex)}
                    style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    onError={() => setImageError(true)}
                  />
                  {propertyImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="property-detail-carousel-prev"
                        onClick={(e) => { e.stopPropagation(); goToPrevImage(); }}
                        style={{
                          position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)', color: 'white', border: 'none', borderRadius: '50%',
                          width: '40px', height: '40px', cursor: 'pointer', fontSize: '20px', zIndex: 2
                        }}
                      >
                        ‹
                      </button>
                      <button
                        type="button"
                        className="property-detail-carousel-next"
                        onClick={(e) => { e.stopPropagation(); goToNextImage(); }}
                        style={{
                          position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                          background: 'rgba(0, 0, 0, 0.5)', color: 'white', border: 'none', borderRadius: '50%',
                          width: '40px', height: '40px', cursor: 'pointer', fontSize: '20px', zIndex: 2
                        }}
                      >
                        ›
                      </button>
                      <div style={{ position: 'absolute', bottom: '15px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px', zIndex: 2 }}>
                        {propertyImages.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                            style={{
                              width: '10px', height: '10px', borderRadius: '50%', border: 'none',
                              background: currentImageIndex === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                              cursor: 'pointer', transition: 'all 0.3s ease'
                            }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="property-detail-image-placeholder" />
            )}
          </div>

          <div className="property-detail-about">
            <h2 className="property-detail-about-title">About this Property</h2>
            <p className="property-detail-about-description">{property.description}</p>
          </div>

          <div className="property-detail-financials">
            <div className="property-detail-financial-card property-detail-financial-card-rent">
              <div className="property-detail-financial-label">Monthly Rent</div>
              <div className="property-detail-financial-value">{property.price}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Sq. ft</div>
              <div className="property-detail-financial-value">{property.sqft}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Type</div>
              <div className="property-detail-financial-value">{property.type}</div>
            </div>
            <div className="property-detail-financial-card">
              <div className="property-detail-financial-label">Deposit</div>
              <div className="property-detail-financial-value">{property.deposit}</div>
            </div>
          </div>

          <div className="property-detail-features">
            <h3 className="property-detail-features-title">Property Features</h3>
            <div className="property-detail-features-grid for-deailsview-cs">
              {amenitiesList.length > 0 ? (
                amenitiesList.map((amenity) => {
                  const hasAmenity = propertyAmenityIds.includes(amenity.id);
                  return (
                    <div key={amenity.id} className="property-detail-feature-item">
                      <span className="property-detail-feature-label">{amenity.name}:</span>
                      <span className="property-detail-feature-value">{hasAmenity ? 'Yes' : 'No'}</span>
                    </div>
                  );
                })
              ) : (
                <div className="property-detail-feature-item">
                  <span className="property-detail-feature-label">Loading amenities...</span>
                </div>
              )}
              <div className="property-detail-feature-item">
                <span className="property-detail-feature-label">Bedroom:</span>
                <span className="property-detail-feature-value">{property.bedrooms}</span>
              </div>
              <div className="property-detail-feature-item">
                <span className="property-detail-feature-label">Bathroom:</span>
                <span className="property-detail-feature-value">{property.bathrooms}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="property-detail-right">
          {/* Lease sent by the landlord — hides itself until there is one to sign */}
          <LeaseSignCard
            propertyId={id}
            tenantName={getSignerName()}
            onSignedChange={setIsLeaseSigned}
          />
          {propertyAllotted ? (
            <div className={`property-screening-section property-screening-section-allotted${allottedToMe ? ' property-screening-section-allocated-to-me' : ''}`}>
              <div className="property-screening-rent-info">
                <div className="property-screening-rent-amount">{property.price}/month</div>
                <div className="property-screening-deposit-text">Security deposit: {property.securityDeposit}</div>
              </div>
              {allottedToMe ? (
                <>
                  <div className="property-screening-allocated-message">
                    <p className="property-screening-allocated-title">🎉 Congratulations, this home is yours!</p>
                    <p className="property-screening-allocated-text">
                      {isLeaseSigned || hasActiveLease
                        ? 'This property has been allotted to you. Set up your monthly rent so it is paid automatically on time, every month.'
                        : 'This property has been allotted to you. Review and sign the lease agreement to set up your monthly rent.'}
                    </p>
                  </div>

                  {hasActiveLease ? (
                    <>
                      <div className="property-screening-rent-subscribed">
                        You are subscribed to monthly rent payments for this property.
                      </div>
                      <button
                        type="button"
                        className="property-screening-cancel-rent-button"
                        onClick={openCancelRentModal}
                        disabled={isCancellingRent}
                      >
                        {isCancellingRent ? 'Cancelling...' : 'Cancel Rent Subscription'}
                      </button>
                      <div className="property-screening-button-subtext">
                        Cancelling stops the monthly rent payments for this property.
                      </div>
                    </>
                  ) : isLeaseSigned ? (
                    <>
                      <button
                        type="button"
                        className="property-screening-start-button"
                        onClick={handleSubscribeMonthlyRent}
                        disabled={isSubscribingRent}
                      >
                        {isSubscribingRent ? 'Setting up subscription...' : 'Subscribe to Monthly Rent'}
                      </button>
                      <div className="property-screening-button-subtext">
                        {property.price} will be charged every month for this property.
                      </div>
                    </>
                  ) : (
                    <div className="property-screening-lease-pending">
                      <p className="property-screening-lease-pending-title">Sign your lease to continue</p>
                      <p className="property-screening-lease-pending-text">
                        The monthly rent subscription unlocks once the lease agreement for this property
                        is signed.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="property-screening-allotted-message">
                  <p className="property-screening-allotted-title">Property no longer available</p>
                  <p className="property-screening-allotted-text">
                    This property has already been allotted. Screening is no longer available for this property.
                  </p>
                </div>
              )}
            </div>
          ) : screeningDone ? (
            <>
              <div className="screening-status-section">
                <h3 className="screening-status-title">Status</h3>
                <div className="screening-status-steps">
                  <div 
                    className="screening-status-progress-line" 
                    aria-hidden="true"
                  />
                  {statusSteps.map((step, index) => (
                    <div key={index} className="screening-status-step">
                      <div className="screening-status-step-indicator">
                        <div className="screening-status-step-dot">
                          <div className="screening-status-step-dot-inner"></div>
                        </div>
                      </div>
                      <div className="screening-status-step-content">
                        <span className="screening-status-step-name">{step.name}</span>
                        {step.checkrStatus ? (
                          <span className={`screening-status-step-badge background-check-status background-check-status-${step.checkrStatus}`}>
                            {step.status}
                            {step.checkrStatus === CHECKR_STATUS.COMPLETED && (
                              <button
                                type="button"
                                className="checkr-view-report-link"
                                onClick={() => navigate(`/tenant/background-check/report?property_id=${id}`)}
                              >
                                (View)
                              </button>
                            )}
                          </span>
                        ) : (
                          <span 
                            className="screening-status-step-badge"
                            style={{ 
                              color: getStatusColor(step.status),
                              backgroundColor: getStatusBgColor(step.status)
                            }}
                          >
                            {step.status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="invitation-background-card">
                <h3 className="invitation-background-title">You have been invited to move forward</h3>
                {backgroundCheckPayment ? (
                  <BackgroundCheckPaymentForm
                    clientSecret={backgroundCheckPayment.clientSecret}
                    amount={backgroundCheckPayment.amount}
                    onPaymentSuccess={handleBackgroundCheckPaymentSuccess}
                    onCancel={() => setBackgroundCheckPayment(null)}
                  />
                ) : backgroundCheckStatus === CHECKR_STATUS.REQUESTED ? (
                  <div className="invitation-background-field">
                    <label className="invitation-background-label">Background check:</label>
                    {backgroundCheckDescription ? (
                      // One bullet per sentence of the API description
                      <ul className="background-check-text background-check-points">
                        {backgroundCheckDescription
                          .split(/(?<=\.)\s+/)
                          .filter(Boolean)
                          .map((point, index) => (
                            <li key={index}>{point}</li>
                          ))}
                      </ul>
                    ) : (
                      <p className="background-check-text">
                        The landlord has requested a background check. Pay the background check fee to start it.
                      </p>
                    )}
                    <button
                      type="button"
                      className="background-check-start-btn"
                      onClick={handleStartBackgroundCheck}
                      disabled={isStartingBackgroundCheck}
                    >
                      {isStartingBackgroundCheck ? 'Please wait...' : 'Start Background Check'}
                    </button>
                  </div>
                ) : (
                <div className="invitation-background-field">
                  <label className="invitation-background-label">Background check:</label>
                  <div className="invitation-background-input-wrapper">
                    {backgroundCheckStatus === CHECKR_STATUS.WAITING_FOR_APPLICANT && backgroundCheckApplicationUrl ? (
                      <button
                        type="button"
                        className="invitation-background-check-link"
                        onClick={() => window.open(backgroundCheckApplicationUrl, '_blank', 'noopener,noreferrer')}
                      >
                        Open Checkr Form
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="invitation-background-check-link"
                        onClick={() => navigate('/tenant/settings/document')}
                      >
                        Begin
                      </button>
                    )}
                  </div>
                </div>
                )}
              </div>
            </>
          ) : (
            <div className="property-screening-section">
              <div className="property-screening-rent-info">
                <div className="property-screening-rent-amount">{property.price}/month</div>
                <div className="property-screening-deposit-text">Security deposit: {property.securityDeposit}</div>
              </div>

              <button 
                className="property-screening-start-button"
                onClick={handleStartScreening}
                disabled={isCheckingDocuments}
              >
                {isCheckingDocuments ? 'Checking documents...' : 'Start Tenant Screening'}
              </button>
              <div className="property-screening-button-subtext">
                {isPaymentDone
                  ? 'Payment is done. Click to begin screening.'
                  : 'Complete screening to apply for this property'}
              </div>
              <div className="property-screening-divider"></div>

              <div className="property-screening-how-it-works">
                <h3 className="property-screening-how-title">How Screening Works</h3>
                <div className="property-screening-steps">
                  <div className="property-screening-step">
                    <div className="property-screening-step-icon">
                      <img 
                        src="/assets/images/CallTenantDetail.svg" 
                        alt="Phone call" 
                        className="property-screening-step-icon-img"
                      />
                    </div>
                    <span className="property-screening-step-text">Automated voice call (5-10 min)</span>
                  </div>
                  <div className="property-screening-step">
                    <div className="property-screening-step-icon">
                      <img 
                        src="/assets/images/UploadTenantDetail.svg" 
                        alt="Upload documents" 
                        className="property-screening-step-icon-img"
                      />
                    </div>
                    <span className="property-screening-step-text">Upload documents for verification</span>
                  </div>
                  <div className="property-screening-step">
                    <div className="property-screening-step-icon">
                      <img 
                        src="/assets/images/UsersTenantDetail.svg" 
                        alt="Reference checks" 
                        className="property-screening-step-icon-img"
                      />
                    </div>
                    <span className="property-screening-step-text">Automated reference checks</span>
                  </div>
                </div>
              </div>

              <div className="property-screening-fee-card">
                <div className="property-screening-fee-label">Screening fee:</div>
                <div className="property-screening-fee-amount">$9.99</div>
              </div>
              <div className="property-screening-fee-note">This screening fee is applicable for all the other properties by this Landlord also</div>
            </div>
          )}
        </div>
      </div>

      <PropertyImageModal
        isOpen={isImageModalOpen}
        onClose={closeImageModal}
        images={propertyImages}
        currentIndex={modalImageIndex}
        onNext={goToNextModalImage}
        onPrev={goToPrevModalImage}
        onIndicatorClick={(index) => setModalImageIndex(index)}
      />

      <TenantDocumentsUploadModal
        isOpen={isDocumentsModalOpen}
        onClose={() => setIsDocumentsModalOpen(false)}
        onUploadSuccess={() => {
          setIsDocumentsModalOpen(false);
          setHasTenantDocuments(true);
          proceedToScreening();
        }}
      />

      {/* Cancel rent subscription confirmation */}
      <Modal
        isOpen={isCancelRentModalOpen}
        onRequestClose={closeCancelRentModal}
        contentLabel="Cancel rent subscription"
        shouldCloseOnOverlayClick={true}
        ariaHideApp={false}
        className="logout-modal"
        overlayClassName="logout-modal-overlay"
      >
        <button className="logout-modal-close" onClick={closeCancelRentModal} type="button" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h2 className="logout-modal-title">Cancel rent subscription</h2>
        <p className="logout-modal-message">
          Are you sure you want to cancel the monthly rent subscription for this property?
        </p>
        <div className="logout-modal-buttons">
          <button type="button" className="logout-modal-cancel" onClick={closeCancelRentModal}>Cancel</button>
          <button type="button" className="logout-modal-confirm" onClick={confirmCancelRentSubscription}>OK</button>
        </div>
      </Modal>
    </div>
  );
};

export default PropertyDetail;
